import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Bot,
  Database,
  FileCheck2,
  Github,
  HelpCircle,
  History,
  Home,
  Mail,
  Package,
  RefreshCcw,
  Terminal,
  ShieldCheck
} from "lucide-react";

export const site = {
  name: "Clovis",
  company: "Clovis Computing",
  domain: "cloviscomputing.com",
  appDomain: "app.cloviscomputing.com",
  githubUrl: "https://github.com/cloviscomputing/clovis",
  githubIssuesUrl: "https://github.com/cloviscomputing/clovis/issues",
  npmUrl: "https://www.npmjs.com/package/clovis",
  docsUrl: "/docs/",
  readmeUrl: "https://github.com/cloviscomputing/clovis#readme",
  version: "1.3.0",
  installCommand: "npm install -g clovis",
  quickCommand: "npx clovis --help",
  nodeRequirement: "Node >=26.3.0",
  refundWindow: "7 days",
  supportEmail: "support@cloviscomputing.com",
  notifyUrl:
    "mailto:support@cloviscomputing.com?subject=Clovis%20early%20access"
};

export type Route = {
  path: string;
  canonicalPath?: string;
  hidden?: boolean;
  label: string;
  title: string;
  description: string;
  icon: LucideIcon;
};

export const routes: Route[] = [
  {
    path: "/",
    label: "Home",
    title: "Turn any AI agent into your personal CFO",
    description:
      "Clovis gives AI agents the financial context to review statements, reconcile a local ledger, and answer questions while your data stays on your machine.",
    icon: Home
  },
  {
    path: "/pricing/",
    canonicalPath: "/",
    hidden: true,
    label: "Install",
    title: "Install Clovis",
    description:
      "Install Clovis from npm and use the CLI or MCP server to review statements, run reports, and keep financial records locally.",
    icon: Package
  },
  {
    path: "/waitlist/",
    canonicalPath: "/",
    hidden: true,
    label: "GitHub",
    title: "Clovis on GitHub",
    description:
      "Clovis is open source on GitHub and published as the public npm package clovis.",
    icon: Github
  },
  {
    path: "/download/",
    canonicalPath: "/",
    hidden: true,
    label: "npm",
    title: "Clovis npm package",
    description:
      "Install the public Clovis npm package for the CLI, MCP server, statement imports, reports, and local agent workflows.",
    icon: Terminal
  },
  {
    path: "/docs/",
    label: "Docs",
    title: "Clovis Documentation",
    description:
      "Generated Clovis CLI, MCP, and agent documentation for the public npm package, including machine-readable tool manifests.",
    icon: BookOpen
  },
  {
    path: "/changelog/",
    canonicalPath: "/docs/changelog/",
    hidden: true,
    label: "Changelog",
    title: "Changelog",
    description:
      "Human-readable release notes for the public Clovis npm package, generated from the packaged changelog.",
    icon: History
  },
  {
    path: "/terms/",
    label: "Terms",
    title: "Terms of Service",
    description: "Terms for using the Clovis website, public package, source repository, CLI, MCP server, and local bookkeeping tools.",
    icon: FileCheck2
  },
  {
    path: "/privacy/",
    label: "Privacy",
    title: "Privacy Policy",
    description: "How Clovis Computing handles website, package, repository, support, and local-ledger privacy.",
    icon: ShieldCheck
  },
  {
    path: "/refund-policy/",
    label: "Refunds",
    title: "Refund Policy",
    description: "Refund status for the public Clovis npm package, source repository, and any future direct purchases.",
    icon: RefreshCcw
  },
  {
    path: "/support/",
    label: "Support",
    title: "Support",
    description:
      "Find Clovis package links, source repository, documentation, issue reporting, and support contact information.",
    icon: HelpCircle
  }
];

export const footerRoutes = routes.filter((route) => route.path !== "/" && !route.hidden);

export function normalizePath(path: string): string {
  if (path === "") return "/";
  if (path === "/index.html") return "/";
  return path.endsWith("/") ? path : `${path}/`;
}

export function routeForPath(path: string): Route {
  const normalized = normalizePath(path);
  return routes.find((route) => route.path === normalized) ?? routes[0];
}

export const painPoints = [
  {
    label: "Hosted apps own the workflow",
    body: "Clovis keeps the ledger in a SQLite database you control instead of a hosted finance account."
  },
  {
    label: "Agents need a safe tool surface",
    body: "The MCP server gives local AI clients explicit bookkeeping tools instead of raw financial free-for-all access."
  },
  {
    label: "Imports need review",
    body: "CSV, QFX, and OFX data can be previewed, checked, reconciled, and committed deliberately."
  },
  {
    label: "Scripts need durable primitives",
    body: "The package exposes a ledger engine, app dispatcher, CLI, and MCP server from one versioned npm package."
  }
];

export const productMoments = [
  {
    label: "Install from npm",
    body: "Use the public `clovis` package globally for the CLI and MCP server, or as a project dependency for embedding."
  },
  {
    label: "Create a local ledger",
    body: "Initialize a SQLite ledger with explicit currency settings and a versioned schema."
  },
  {
    label: "Import and reconcile",
    body: "Preview CSV, QFX, or OFX files, inspect what is new, and reconcile before committing changes."
  },
  {
    label: "Ask through MCP",
    body: "Expose finance-safe local tools to an MCP client for reports, effect-line queries, imports, categorization, and maintenance."
  },
  {
    label: "Script or embed",
    body: "Use Node.js exports when you want the ledger engine inside another local application."
  }
];

export const featureBenefitItems = [
  {
    area: "Package surfaces",
    feature: "CLI",
    benefit: "Run bookkeeping commands locally",
    body: "Initialize ledgers, add transactions, import files, run reports, export data, and perform diagnostics."
  },
  {
    area: "Package surfaces",
    feature: "MCP server",
    benefit: "Give agents a bounded finance toolkit",
    body: "Expose tools for reports, imports, reconciliation, categorization, backups, and integrity checks."
  },
  {
    area: "Package surfaces",
    feature: "Node.js APIs",
    benefit: "Embed the ledger engine",
    body: "Use package exports from `clovis`, `clovis/core`, `clovis/app`, and `clovis/mcp`."
  },
  {
    area: "Ledger core",
    feature: "SQLite database",
    benefit: "Own the records",
    body: "Keep books in a local SQLite file instead of depending on a hosted finance account."
  },
  {
    area: "Ledger core",
    feature: "Double-entry journals",
    benefit: "Balanced by design",
    body: "The engine enforces balanced journals per asset and stores quantities as scaled integers."
  },
  {
    area: "Ledger core",
    feature: "Versioned schema",
    benefit: "Stable upgrade path",
    body: "The current schema is versioned and older ledgers migrate on open."
  },
  {
    area: "Imports and review",
    feature: "CSV, QFX, and OFX",
    benefit: "Bring bank files into review",
    body: "Preview statement files and inspect new rows before committing them."
  },
  {
    area: "Imports and review",
    feature: "Reconciliation",
    benefit: "Match records to source statements",
    body: "Find duplicates, pending items, uncategorized rows, and suspicious categorization."
  },
  {
    area: "Agent workflows",
    feature: "Tool registry",
    benefit: "Discover exactly what the MCP server can do",
    body: "Agents can inspect signatures, aliases, safety hints, and file-access configuration."
  },
  {
    area: "Agent workflows",
    feature: "Effect-line queries",
    benefit: "Read transactions by accounting effect",
    body: "Use `query_effects` to inspect income, expenses, transfers, refunds, corrections, and category groups."
  },
  {
    area: "Reports",
    feature: "Financial overview",
    benefit: "Ask practical questions",
    body: "Use the CLI or MCP server for balances, net worth, income statement, cash flow, spending, budgets, and runway."
  },
  {
    area: "Reports",
    feature: "Maintenance",
    benefit: "Keep books healthy",
    body: "Use backups, exports, period closes, snapshots, and integrity checks in local workflows."
  }
];

export const objectionItems = [
  {
    label: "Is this a hosted finance app?",
    body: "No. Clovis is a local-first toolkit. The package runs against a SQLite ledger you control."
  },
  {
    label: "Is this only for humans?",
    body: "No. Clovis is built for people and local agents through the CLI, Node.js APIs, and MCP server."
  },
  {
    label: "Is this production-stable?",
    body: "Yes. Clovis has a stable 1.x public package contract for documented CLI commands, MCP tools, package exports, and readable migrations."
  },
  {
    label: "Does it replace professional advice?",
    body: "No. Clovis organizes bookkeeping records and reports. It is not financial, tax, legal, or investment advice."
  },
  {
    label: "Does it require a bank login?",
    body: "No. Clovis is designed for files and local ledgers; imports can use CSV, QFX, and OFX statement files."
  }
];

export const proofPoints = [
  {
    label: "npm package",
    value: `v${site.version}`,
    body: "Published publicly as `clovis`."
  },
  {
    label: "Repository",
    value: "Public",
    body: "Source, README, issues, and CI live on GitHub."
  },
  {
    label: "License",
    value: "AGPL-3.0",
    body: "Open-source license for the public toolkit."
  },
  {
    label: "Runtime",
    value: "Node 26+",
    body: "Current package engine requirement."
  }
];

export const assuranceItems = [
  "Local SQLite ledger instead of a hosted transaction account.",
  "Double-entry journals, versioned schema, migrations, and integrity checks.",
  "CLI and MCP tools for imports, reconciliation, effect-line queries, reporting, categorization, backups, and exports.",
  "File-based imports for CSV, QFX, and OFX workflows.",
  "No bank-login aggregator required for the core workflow.",
  "Public source and package metadata on GitHub and npm."
];

export const faqItems = [
  {
    question: "How do I install Clovis?",
    answer: `Install the CLI and MCP server with \`${site.installCommand}\`, or run \`${site.quickCommand}\` without a global install.`
  },
  {
    question: "What is Clovis?",
    answer:
      "Clovis is a local-first bookkeeping toolkit with a CLI, MCP server, Node.js APIs, and SQLite ledger engine."
  },
  {
    question: "What can Clovis import?",
    answer:
      "CSV, QFX, and OFX transaction files. Imported records can be previewed and reviewed before commit."
  },
  {
    question: "Is this a Clovis cloud account?",
    answer:
      "No. Clovis is designed around a local SQLite ledger file you control."
  },
  {
    question: "Why MCP?",
    answer:
      "The MCP server lets local AI clients use explicit bookkeeping tools for reports, imports, reconciliation, categorization, and diagnostics."
  },
  {
    question: "Is it stable?",
    answer:
      "Yes. The public package is in the 1.x line with documented compatibility for public exports, CLI commands, MCP tools, and readable migrations."
  },
  {
    question: "Is Clovis financial advice?",
    answer:
      "No. Clovis organizes bookkeeping records and reports. It is not financial, tax, legal, or investment advice."
  },
  {
    question: "Who is Clovis for?",
    answer:
      "Developers, operators, finance power users, and local-agent workflows that need a durable ledger instead of a hosted finance app."
  }
];

export const supportLinks = [
  {
    label: "Read the docs",
    href: site.docsUrl,
    icon: BookOpen
  },
  {
    label: "Read the README",
    href: site.readmeUrl,
    icon: FileCheck2
  },
  {
    label: "View source on GitHub",
    href: site.githubUrl,
    icon: Github
  },
  {
    label: "Report a GitHub issue",
    href: site.githubIssuesUrl,
    icon: HelpCircle
  },
  {
    label: "Open npm package",
    href: site.npmUrl,
    icon: Package
  },
  {
    label: "Email support",
    href: `mailto:${site.supportEmail}`,
    icon: Mail
  }
];
